import { useState } from 'react';
import { useQuery } from '@apollo/client/react';
import { useTranslation } from 'react-i18next';
import { ChevronDown } from 'lucide-react';
import { cn, Collapsible, CollapsibleContent, CollapsibleTrigger } from '@scio3/ui';
import { GET_INVOICES_UNPAID } from '@scio3/graphql-client';
import { safeFormatDate } from '@/lib/safe-format';
import { formatCurrencyAmount } from '../utils/plan-helpers';

type UnpaidInvoice = {
  id: string;
  number?: string;
  amount: number;
  created: number;
  status: string;
  collection_method: string;
};

type Props = {
  subAccountId?: string;
  teamId?: string;
  currency?: string;
};

/**
 * Unpaid Invoices collapsible section — matching scio1's invoicesUnPaidComponent.
 * Shows a table of open Stripe invoices with total amount due.
 * Displayed on the Payment Settings page above billing sections.
 */
export function UnpaidInvoicesSection({ subAccountId, teamId, currency = 'usd' }: Props) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(true);

  const { data, loading } = useQuery<{
    getInvoicesUnpaid: { status: boolean; invoices: UnpaidInvoice[] };
  }>(GET_INVOICES_UNPAID, {
    variables: { subAccountId, teamId },
    fetchPolicy: 'network-only',
  });

  const invoices = (data?.getInvoicesUnpaid?.invoices ?? []) as UnpaidInvoice[];
  const total = invoices.reduce((sum, inv) => sum + inv.amount, 0);

  // Wait for API to finish before deciding to show/hide — prevents flash
  if (loading || invoices.length === 0) return null;

  return (
    <Collapsible open={open} onOpenChange={setOpen}>
      <section
        className="rounded-lg border border-border bg-card"
        data-testid="unpaid-invoices-section"
      >
        <CollapsibleTrigger asChild>
          <div className="flex items-center justify-between px-8 py-6 cursor-pointer">
            <h2 className="text-base font-semibold">{t('Unpaid Invoices')}</h2>
            <ChevronDown
              className={cn(
                'h-4 w-4 text-muted-foreground transition-transform',
                open && 'rotate-180',
              )}
            />
          </div>
        </CollapsibleTrigger>
        <CollapsibleContent>
          <div className="px-8 pb-8">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b">
                  <th className="py-2 text-left font-medium">{t('Invoice ID')}</th>
                  <th className="py-2 text-left font-medium">{t('Created')}</th>
                  <th className="py-2 text-right font-medium">{t('Amount')}</th>
                </tr>
              </thead>
              <tbody>
                {invoices.map((inv) => (
                  <tr key={inv.id} className="border-b">
                    <td className="py-2">{inv.number ?? inv.id}</td>
                    <td className="py-2">
                      {safeFormatDate(new Date(inv.created * 1000), 'MM/dd/yyyy')}
                    </td>
                    <td className="py-2 text-right">
                      {formatCurrencyAmount(inv.amount / 100, currency)}
                    </td>
                  </tr>
                ))}
                <tr className="border-b font-medium">
                  <td className="py-2" />
                  <td className="py-2">{t('Amount due')}</td>
                  <td className="py-2 text-right">{formatCurrencyAmount(total / 100, currency)}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </CollapsibleContent>
      </section>
    </Collapsible>
  );
}
